import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, CalendarDays, Radar, Bookmark, MessagesSquare, Archive,
  ShieldCheck, Sparkles, Check, Users, LineChart, Clock
} from "lucide-react";

const PRODUCTS = [
  {
    slug: "today",
    title: "Today",
    icon: CalendarDays,
    desc: "Your daily investigation hub. Trade of the Day, Daily Brief, watchlist movement, and top live signals — everything you need to start your session.",
    screenshot: "/screenshots/today.png",
  },
  {
    slug: "signals",
    title: "Signals",
    icon: Radar,
    desc: "Consensus signals ranked by confidence and actionability. Filter by market scope, archetype, and lifecycle. Every signal decomposable to the traders driving it.",
    screenshot: "/screenshots/signals.png",
  },
  {
    slug: "traders",
    title: "Traders",
    icon: Users,
    desc: "Leaderboard-ranked traders with risk-adjusted scores, category performance, and archetype inference. Every position, every market — sourced from leaderboard proof and on-chain holdings.",
    screenshot: "/screenshots/traders.png",
  },
  {
    slug: "watchlists",
    title: "Watchlists",
    icon: Bookmark,
    desc: "Track markets and traders that matter to you. Daily digest previews, movement indicators, and AI copilot that explains what changed since your last visit.",
    screenshot: "/screenshots/watchlists.png",
  },
  {
    slug: "chat",
    title: "Chat",
    icon: MessagesSquare,
    desc: "Persistent intelligence chat. Ask questions about markets, traders, or signals and get grounded answers. Saved sessions with full history.",
    screenshot: "/screenshots/chat.png",
  },
  {
    slug: "history",
    title: "Intelligence History",
    icon: Archive,
    desc: "Every brief, pick, digest, and AI report in one timeline. Search, filter, and revisit past analysis without losing context.",
    screenshot: "/screenshots/history.png",
  },
];

const PLANS = [
  {
    name: "Free",
    price: "$0",
    period: "forever",
    desc: "Sample the product and understand the workflow.",
    features: [
      "1 watchlist",
      "10 tracked items",
      "3 Chat AI queries/day",
      "10 history items",
      "Market & trader browsing",
      "Live signals & consensus",
    ],
    cta: "Get started",
    to: "/register",
    highlight: false,
  },
  {
    name: "Beta",
    price: "Invite",
    period: "limited",
    desc: "Invite-only validation before public pricing.",
    features: [
      "10 watchlists",
      "100 tracked items",
      "25 alerts",
      "50 Chat AI queries/day",
      "20 Watchlist AI queries/day",
      "75 history items",
    ],
    cta: "Request access",
    to: "/register",
    highlight: true,
  },
  {
    name: "Pro",
    price: "$39",
    period: "/month",
    desc: "Primary paid individual plan for active traders.",
    features: [
      "25 watchlists",
      "250 tracked items",
      "100 alerts",
      "Unlimited Chat AI",
      "50 Watchlist AI queries/day",
      "Full history",
    ],
    cta: "Coming soon",
    to: "#",
    highlight: false,
  },
  {
    name: "Power",
    price: "$99",
    period: "/month",
    desc: "For power users and teams who need maximum capacity.",
    features: [
      "100 watchlists",
      "1,000 tracked items",
      "500 alerts",
      "Unlimited Chat AI",
      "Unlimited Watchlist AI",
      "Full history + priority support",
    ],
    cta: "Coming soon",
    to: "#",
    highlight: false,
  },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#09090b] text-zinc-100">
      {/* Nav */}
      <header className="sticky top-0 z-30 blur-surface border-b border-zinc-800">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-5 h-14">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-sm bg-zinc-100 text-zinc-950 grid place-items-center">
              <Sparkles size={13} />
            </div>
            <span className="font-heading font-bold tracking-tight">MarketTrace</span>
          </Link>
          <nav className="flex items-center gap-6 text-sm text-zinc-400">
            <Link to="/methodology" className="hover:text-zinc-100 transition-colors">Methodology</Link>
            <Link to="/login" className="hover:text-zinc-100 transition-colors">Sign in</Link>
            <Link
              to="/register"
              className="bg-zinc-100 text-zinc-950 px-3 py-1.5 rounded-md hover:bg-white transition-colors"
            >
              Get started
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-zinc-800">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1526289034009-0240ddb68ce3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhdGElMjBhcmNoaXRlY3R1cmUlMjBkYXJrfGVufDB8fHx8MTc4MzczMTU0NHww&ixlib=rb-4.1.0&q=85')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-[#09090b]/85" />
        <div className="absolute inset-0 noise-overlay" />
        <div className="relative max-w-6xl mx-auto px-5 py-24 md:py-32">
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-4">
            Polymarket · Trader Intelligence · Read-only
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter max-w-3xl leading-[1.05]">
            Track what Polymarket&apos;s strongest traders are doing.
          </h1>
          <p className="mt-6 max-w-2xl text-zinc-400 text-base leading-relaxed">
            MarketTrace transforms public market and wallet activity into trader rankings, position
            changes, consensus signals, and actionability analysis. No trading. No custody. Just
            transparent decision-support.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to="/register"
              className="inline-flex items-center gap-2 bg-zinc-100 text-zinc-950 px-5 py-2.5 rounded-md hover:bg-white transition-colors text-sm font-semibold"
            >
              Explore the Dashboard <ArrowRight size={15} />
            </Link>
            <Link
              to="/demo"
              className="inline-flex items-center gap-2 border border-emerald-700 hover:border-emerald-500 text-emerald-300 px-5 py-2.5 rounded-md transition-colors text-sm"
            >
              Try Demo
            </Link>
            <Link
              to="/methodology"
              className="inline-flex items-center gap-2 border border-zinc-700 hover:border-zinc-500 px-5 py-2.5 rounded-md transition-colors text-sm text-zinc-300"
            >
              View Methodology
            </Link>
          </div>
        </div>
      </section>

      {/* Demo video */}
      <section className="border-b border-zinc-800 py-20">
        <div className="max-w-5xl mx-auto px-5">
          <div className="text-center mb-10">
            <h2 className="font-heading text-3xl font-bold tracking-tight">See it in action</h2>
            <p className="mt-3 text-zinc-500 max-w-xl mx-auto">
              A 4-minute walkthrough of MarketTrace — from the dashboard to signals, traders, watchlists, and more.
            </p>
          </div>
          <div className="rounded-xl border border-zinc-800 overflow-hidden shadow-2xl bg-black">
            <video
              controls
              poster="/screenshots/dashboard.png"
              className="w-full h-auto block"
              preload="metadata"
            >
              <source src="/demo.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>

      {/* Product cards with screenshots */}
      <section className="border-b border-zinc-800 py-20">
        <div className="max-w-6xl mx-auto px-5">
          <div className="text-center mb-14">
            <h2 className="font-heading text-3xl font-bold tracking-tight">See the product</h2>
            <p className="mt-3 text-zinc-500 max-w-xl mx-auto">
              Every screen in MarketTrace is built around one question: what changed since your last visit?
            </p>
          </div>
          <div className="space-y-20">
            {PRODUCTS.map((p, i) => (
              <div
                key={p.slug}
                className={`flex flex-col ${i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"} gap-8 md:gap-12 items-center`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-3">
                    <p.icon size={18} className="text-zinc-400" />
                    <h3 className="font-heading text-xl font-semibold text-zinc-100">{p.title}</h3>
                  </div>
                  <p className="text-zinc-400 leading-relaxed">{p.desc}</p>
                </div>
                <div className="flex-1 w-full">
                  <div className="rounded-lg border border-zinc-800 overflow-hidden shadow-2xl">
                    <img
                      src={p.screenshot}
                      alt={`${p.title} screenshot`}
                      className="w-full h-auto block"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-b border-zinc-800 py-20">
        <div className="max-w-6xl mx-auto px-5">
          <div className="text-center mb-14">
            <h2 className="font-heading text-3xl font-bold tracking-tight">How it works</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-10">
            {[
              { n: "01", t: "Public data ingestion", d: "Live sync from Polymarket Gamma, Data, and CLOB APIs. Raw payloads preserved. Every market, every trader, every position." },
              { n: "02", t: "Trader scoring & consensus", d: "Position reconstruction, risk-adjusted scoring, and per-market consensus. When multiple high-quality traders converge on the same outcome, we surface it." },
              { n: "03", t: "Signals with actionability", d: "Signals labeled with lifecycle, confidence, and remaining edge. Filter by market scope — current, historical, or all." },
            ].map((s) => (
              <div key={s.n}>
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono-num">{s.n}</div>
                <div className="mt-2 font-heading font-semibold text-zinc-100">{s.t}</div>
                <div className="mt-2 text-sm text-zinc-500 leading-relaxed">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="border-b border-zinc-800 py-20" id="pricing">
        <div className="max-w-6xl mx-auto px-5">
          <div className="text-center mb-14">
            <h2 className="font-heading text-3xl font-bold tracking-tight">Pricing</h2>
            <p className="mt-3 text-zinc-500 max-w-xl mx-auto">
              Start free. Upgrade when you need more capacity. Beta is invite-only during validation.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {PLANS.map((plan) => (
              <div
                key={plan.name}
                className={`rounded-lg border p-6 flex flex-col ${
                  plan.highlight
                    ? "border-zinc-500 bg-zinc-900/50"
                    : "border-zinc-800 bg-[#0b0b0f]"
                }`}
              >
                <div className="text-sm font-semibold text-zinc-100">{plan.name}</div>
                <div className="mt-2">
                  <span className="text-2xl font-bold text-zinc-100">{plan.price}</span>
                  <span className="text-sm text-zinc-500 ml-1">{plan.period}</span>
                </div>
                <p className="mt-2 text-xs text-zinc-500">{plan.desc}</p>
                <ul className="mt-5 space-y-2 flex-1">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-xs text-zinc-400">
                      <Check size={12} className="mt-0.5 text-emerald-500 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to={plan.to}
                  className={`mt-6 block text-center rounded-md px-3 py-2 text-xs font-medium transition-colors ${
                    plan.highlight
                      ? "bg-zinc-100 text-zinc-950 hover:bg-white"
                      : "border border-zinc-700 text-zinc-300 hover:border-zinc-500"
                  }`}
                >
                  {plan.cta}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Waitlist signup */}
      <section className="border-b border-zinc-800 py-20" id="waitlist">
        <div className="max-w-lg mx-auto px-5 text-center">
          <h2 className="font-heading text-2xl font-bold tracking-tight">Join the beta waitlist</h2>
          <p className="mt-3 text-sm text-zinc-500">
            Get early access to MarketTrace. Beta users get expanded limits and direct input on the product roadmap.
          </p>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              const email = e.target.email.value;
              const name = e.target.name.value;
              const btn = e.target.querySelector("button");
              btn.disabled = true;
              btn.textContent = "Joining…";
              try {
                const r = await fetch("/api/beta/waitlist", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ email, name, source: "landing-page" }),
                });
                const d = await r.json();
                if (d.ok) {
                  btn.textContent = "You're on the list!";
                  btn.className = "mt-4 w-full rounded-md bg-emerald-600 text-white px-4 py-2.5 text-sm font-medium opacity-70 cursor-default";
                  e.target.email.disabled = true;
                  e.target.name.disabled = true;
                } else {
                  btn.textContent = "Already on the list!";
                  btn.className = "mt-4 w-full rounded-md bg-zinc-600 text-white px-4 py-2.5 text-sm font-medium opacity-70 cursor-default";
                }
              } catch {
                btn.textContent = "Something went wrong — try again";
                btn.disabled = false;
              }
            }}
            className="mt-8 space-y-3"
          >
            <input
              type="text"
              name="name"
              placeholder="Your name (optional)"
              className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-4 py-2.5 text-sm text-zinc-100 focus:outline-none focus:ring-1 focus:ring-zinc-600 placeholder-zinc-600"
            />
            <input
              type="email"
              name="email"
              required
              placeholder="you@email.com"
              className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-4 py-2.5 text-sm text-zinc-100 focus:outline-none focus:ring-1 focus:ring-zinc-600 placeholder-zinc-600"
            />
            <button
              type="submit"
              className="mt-4 w-full rounded-md bg-zinc-100 text-zinc-950 px-4 py-2.5 text-sm font-medium hover:bg-white transition-colors"
            >
              Join the waitlist
            </button>
          </form>
        </div>
      </section>
      <section className="border-b border-zinc-800 py-16">
        <div className="max-w-4xl mx-auto px-5">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl font-bold tracking-tight">Trust & safety</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { icon: ShieldCheck, title: "Read-only, always", body: "MarketTrace never executes trades, holds funds, or manages wallets. Decision-support only." },
              { icon: LineChart, title: "Transparent methodology", body: "Every score is decomposable. Missing data is labeled, never invented. No opaque black boxes." },
              { icon: Clock, title: "Data freshness guaranteed", body: "Live sync from Polymarket APIs. Stale markets are labeled and excluded from actionable signals." },
              { icon: Users, title: "No copy-trading", body: "We surface consensus, not trade signals. You decide what to do with the information." },
            ].map((t) => (
              <div key={t.title} className="border border-zinc-800 rounded-md p-5">
                <t.icon size={18} className="text-zinc-300 mb-3" />
                <div className="font-heading font-semibold text-sm text-zinc-100">{t.title}</div>
                <div className="text-xs text-zinc-500 mt-1 leading-relaxed">{t.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-5 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">
            See who&apos;s converging — before the price moves.
          </h2>
          <p className="mt-4 text-zinc-500 max-w-lg mx-auto">
            Start free. No credit card. No trading. Just transparent decision-support for Polymarket.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/register"
              className="inline-flex items-center gap-2 bg-zinc-100 text-zinc-950 px-6 py-3 rounded-md hover:bg-white text-sm font-semibold"
            >
              Create your account <ArrowRight size={15} />
            </Link>
            <Link
              to="/demo"
              className="inline-flex items-center gap-2 border border-emerald-700 hover:border-emerald-500 text-emerald-300 px-6 py-3 rounded-md transition-colors text-sm"
            >
              Try Demo
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-zinc-800 py-8 text-center text-xs text-zinc-600">
        © MarketTrace · Read-only analytics for Polymarket public data
      </footer>
    </div>
  );
}


